import { supabase } from "@/integrations/supabase/client";

export type TaxStatus = "draft" | "submitted" | "verified" | "rejected" | "expired";
export type TaxFormType = "w9" | "w8ben";

export type TaxProfileSummary = {
  form_type: TaxFormType;
  status: TaxStatus;
  legal_name: string;
  business_name: string | null;
  country: string;
  tax_classification: string | null;
  tin_kind: "ssn" | "ein" | "foreign" | null;
  tin_last4: string | null;
  address_preview: string | null;
  treaty_country: string | null;
  signed_name: string | null;
  signed_at: string | null;
  submitted_at: string | null;
  verified_at: string | null;
  expires_at: string | null;
  rejected_at: string | null;
  rejected_reason: string | null;
};

export type TaxFormPayload = {
  form_type: TaxFormType;
  legal_name: string;
  business_name?: string;
  country: string;
  tax_classification?: string;
  tin_kind?: "ssn" | "ein" | "foreign";
  tin?: string;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_region?: string;
  address_postal?: string;
  treaty_country?: string;
  foreign_tin?: string;
  date_of_birth?: string;
  signed_name?: string;
};

async function call(action: string, extra: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("affiliate-tax-profile", {
    body: { action, ...extra },
  });
  if (error) throw new Error(error.message);
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function getMyTaxProfile(): Promise<TaxProfileSummary | null> {
  const data = await call("get_self");
  return (data?.profile ?? null) as TaxProfileSummary | null;
}

export async function saveTaxDraft(profile: TaxFormPayload) {
  return call("save_draft", { profile });
}

export async function submitTaxForm(profile: TaxFormPayload) {
  return call("submit", { profile });
}

export type AdminTaxView = {
  profile: TaxProfileSummary & {
    user_id: string;
    signed_ip: string | null;
    signed_user_agent: string | null;
    verified_by_user_id: string | null;
  };
  tin: string | null;
  foreign_tin: string | null;
  address: {
    line1?: string;
    line2?: string | null;
    city?: string;
    region?: string | null;
    postal?: string | null;
    country?: string;
  } | null;
};

export async function adminGetTaxProfile(userId: string): Promise<AdminTaxView | null> {
  const data = await call("admin_get", { user_id: userId });
  return data?.profile ? (data as AdminTaxView) : null;
}

export function formatStatus(s: TaxStatus): { label: string; tone: "default" | "warning" | "success" | "destructive" } {
  switch (s) {
    case "draft": return { label: "Draft", tone: "default" };
    case "submitted": return { label: "Awaiting review", tone: "warning" };
    case "verified": return { label: "Verified", tone: "success" };
    case "rejected": return { label: "Rejected", tone: "destructive" };
    case "expired": return { label: "Expired", tone: "destructive" };
  }
}
